import { useState } from 'react';
import {
  Users as UsersIcon,
  Plus,
  Shield,
  ShieldCheck,
  HeadphonesIcon,
  MoreHorizontal,
  Loader2,
  Eye,
  EyeOff,
} from 'lucide-react';
import {
  useAdminUsers,
  useCreateAdminUser,
  useUpdateAdminUser,
  useDeleteAdminUser,
  useAdminUser,
  AdminRole,
  AdminUser
} from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

export default function Users() {
  const { data: currentUser } = useAdminUser();
  const { data: users, isLoading } = useAdminUsers();
  const createUser = useCreateAdminUser();
  const updateUser = useUpdateAdminUser();
  const deleteUser = useDeleteAdminUser();

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin' as AdminRole,
  });
  const [formError, setFormError] = useState<string | null>(null);

  const isSuperAdmin = currentUser?.role === 'superadmin';

  const getRoleIcon = (role: AdminRole) => {
    switch (role) {
      case 'superadmin':
        return <ShieldCheck className="h-4 w-4 text-orange-500" />;
      case 'admin':
        return <Shield className="h-4 w-4 text-blue-500" />;
      case 'support':
        return <HeadphonesIcon className="h-4 w-4 text-green-500" />;
    }
  };

  const getRoleBadge = (role: AdminRole) => {
    const styles: Record<AdminRole, string> = {
      superadmin: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
      admin: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      support: 'bg-green-500/10 text-green-400 border-green-500/20',
    };
    return (
      <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium border flex items-center gap-1', styles[role])}>
        {getRoleIcon(role)}
        {role}
      </span>
    );
  };

  const handleAddUser = async () => {
    setFormError(null);

    if (!formData.name.trim()) {
      setFormError('Name is required');
      return;
    }
    if (!formData.email.trim()) {
      setFormError('Email is required');
      return;
    }
    if (!formData.password || formData.password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }

    try {
      await createUser.mutateAsync({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
      });
      setShowAddModal(false);
      setFormData({ name: '', email: '', password: '', role: 'admin' });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create user');
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    setFormError(null);

    try {
      await updateUser.mutateAsync({
        id: editingUser.id,
        updates: {
          name: formData.name,
          role: formData.role,
        },
      });
      setEditingUser(null);
      setFormData({ name: '', email: '', password: '', role: 'admin' });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  const handleDeactivateUser = async (user: AdminUser) => {
    if (!confirm(`Are you sure you want to deactivate ${user.name}? They will no longer be able to access the admin dashboard.`)) {
      return;
    }

    try {
      await deleteUser.mutateAsync(user.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to deactivate user');
    }
  };

  const handleReactivateUser = async (user: AdminUser) => {
    try {
      await updateUser.mutateAsync({
        id: user.id,
        updates: { is_active: true },
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to reactivate user');
    }
  };

  const openEditModal = (user: AdminUser) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
    });
    setFormError(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const activeUsers = users?.filter(u => u.is_active) || [];
  const inactiveUsers = users?.filter(u => !u.is_active) || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Admin Users</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage who can access the admin dashboard
          </p>
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => {
              setFormData({ name: '', email: '', password: '', role: 'admin' });
              setFormError(null);
              setShowAddModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add User
          </button>
        )}
      </div>

      {/* Active Users */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
            <UsersIcon className="h-4 w-4" />
            Active Users ({activeUsers.length})
          </h3>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {activeUsers.map((user) => (
            <div
              key={user.id}
              className="px-4 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    {user.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{user.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {getRoleBadge(user.role)}
                {user.last_login_at && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 hidden md:block">
                    Last login: {new Date(user.last_login_at).toLocaleDateString()}
                  </span>
                )}
                {isSuperAdmin && user.id !== currentUser?.id && (
                  <div className="relative group">
                    <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                    <div className="absolute right-0 bottom-full mb-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 min-w-[150px]">
                      <button
                        onClick={() => openEditModal(user)}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg"
                      >
                        Edit User
                      </button>
                      {user.role !== 'superadmin' && (
                        <button
                          onClick={() => handleDeactivateUser(user)}
                          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 last:rounded-b-lg"
                        >
                          Deactivate
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {user.id === currentUser?.id && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                    You
                  </span>
                )}
              </div>
            </div>
          ))}
          {activeUsers.length === 0 && (
            <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
              No active users found
            </div>
          )}
        </div>
      </div>

      {/* Inactive Users */}
      {inactiveUsers.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden opacity-75">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <h3 className="font-medium text-gray-500 dark:text-gray-400 flex items-center gap-2">
              Inactive Users ({inactiveUsers.length})
            </h3>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {inactiveUsers.map((user) => (
              <div
                key={user.id}
                className="px-4 py-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center opacity-50">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-500 dark:text-gray-400">{user.name}</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500">{user.email}</p>
                  </div>
                </div>
                {isSuperAdmin && (
                  <button
                    onClick={() => handleReactivateUser(user)}
                    className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                  >
                    Reactivate
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || editingUser) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => { setShowAddModal(false); setEditingUser(null); }} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingUser ? 'Edit User' : 'Add New User'}
            </h2>

            {formError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                {formError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="John Doe"
                />
              </div>

              {!editingUser && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="user@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 pr-10"
                        placeholder="Min 8 characters"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Role *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as AdminRole }))}
                  className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  disabled={editingUser?.role === 'superadmin'}
                >
                  <option value="admin">Admin - Full access to client management</option>
                  <option value="support">Support - Read-only access</option>
                  {(editingUser?.role === 'superadmin' || !editingUser) && (
                    <option value="superadmin" disabled={!editingUser}>Superadmin - Full system access</option>
                  )}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {formData.role === 'admin' && 'Can manage clients, subscriptions, and billing'}
                  {formData.role === 'support' && 'View-only access for support purposes'}
                  {formData.role === 'superadmin' && 'Full access including user management'}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowAddModal(false); setEditingUser(null); }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={editingUser ? handleUpdateUser : handleAddUser}
                disabled={createUser.isPending || updateUser.isPending}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {(createUser.isPending || updateUser.isPending) && <Loader2 className="h-4 w-4 animate-spin" />}
                {editingUser ? 'Save Changes' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

